import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import {
  dashboardPaciente,
  abrirArchivoURL,
  deleteAnexoEva
} from '../services/api';
import moment from 'moment';
import AnexoModal from '../components/AnexoModal';
import AnexoViewerModal from '../components/AnexoViewerModal';
import { confirmar } from './ConfirmModal';
import { toast } from 'react-toastify';
import '../styles/styles.css';
import '@fortawesome/fontawesome-free/css/all.min.css';

const PacienteDashboard = () => {
  const { currentUser } = useContext(UserContext);
  const navigate = useNavigate();

  const [datosDashboard, setDatosDashboard] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [errorText, setErrorText] = useState(null);
  const [modalSubirAbierto, setModalSubirAbierto] = useState(false);
  const [evaluacionSeleccionada, setEvaluacionSeleccionada] = useState(null);

  // Anexo que se está visualizando en el AnexoViewerModal ({ idEva, anexo, pendiente })
  const [anexoVer, setAnexoVer] = useState(null);

  const cargarDashboard = async () => {
    try {
      setCargando(true);
      const res = await dashboardPaciente();
      setDatosDashboard(res.data);
    } catch (err) {
      console.error('Error al cargar el dashboard del paciente:', err);
      setErrorText('No fue posible cargar tu información.');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarDashboard();
  }, []);

  const handleCerrarModalAnexo = () => {
    setAnexoVer(null);
  };

  const handleVerAnexo = (idEva, anexo, pendiente) => {
    setAnexoVer({ idEva, anexo, pendiente });
  };

  const abrirModalSubida = (pdf) => {
    setEvaluacionSeleccionada(pdf);
    setModalSubirAbierto(true);
  };

  const handleEliminarAnexo = async (idEva, idAnexo, e) => {
    if (e) e.stopPropagation();
    if (!(await confirmar('¿Estás seguro de que deseas eliminar este anexo?', { variante: 'peligro' }))) return;
    try {
      await deleteAnexoEva(idEva, idAnexo);
      if (anexoVer?.anexo?.id_anexo === idAnexo) handleCerrarModalAnexo();
      cargarDashboard();
    } catch (err) {
      console.error('Error al eliminar anexo:', err);
      toast.error(err.response?.data?.message || 'Error al eliminar el anexo.');
    }
  };

  if (cargando) {
    return (
      <div className="content-wrapper">
        <p>Cargando tu información...</p>
      </div>
    );
  }

  if (errorText) {
    return (
      <div className="content-wrapper">
        <p className="required-text">{errorText}</p>
      </div>
    );
  }

  if (!datosDashboard) return null;

  const { paciente, pdfs } = datosDashboard;

  return (
    <div className="content-wrapper">
      <h1>Hola, {paciente?.nombre ? `${paciente.nombre} ${paciente.apellido_p ?? ''}` : (currentUser?.email ?? 'Paciente')}</h1>
      <p>Bienvenido a tu perfil.</p>
      <button
        type="button"
        className="btn-create"
        onClick={() => navigate('/perfil/detalles')}
      >
        Ver Detalles Perfil
      </button>

      <div className="container">
        <h2>Mis Fichas Médicas (PDFs)</h2>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Ficha Médica</th>
                <th>Anexos Adjuntos</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(pdfs) && pdfs.length > 0 ? (
                pdfs.map((pdf) => (
                  <tr key={pdf.id_pdf ?? `eva-${pdf.id_eva}`} className={pdf.pendiente ? "evaluacion-pendiente" : ""}>
                    <td>{pdf.fecha ? moment(pdf.fecha).format('DD-MM-YYYY') : '-'}</td>
                    <td>
                      {pdf.archivo_url ? (
                        <button
                          type="button"
                          onClick={() => abrirArchivoURL(pdf.archivo_url)}
                          className="btn-icon"
                        >
                          <i className="fas fa-eye"></i> Ver / Descargar
                        </button>
                      ) : (
                        <span className="text-muted">Ficha aún no generada</span>
                      )}
                    </td>
                    <td>
                      {Array.isArray(pdf.anexos) && pdf.anexos.length > 0 ? (
                        <div className="anexos-lista">
                          {pdf.anexos.map((anexo) => (
                            <div key={anexo.id_anexo} className="anexo-item-row">
                              <button
                                type="button"
                                onClick={() => handleVerAnexo(pdf.id_eva, anexo, pdf.pendiente)}
                                className="btn-icon btn-secondary btn-sm anexo-nombre-btn"
                                title={anexo.observaciones || anexo.original_name}
                              >
                                <i className="fas fa-paperclip"></i>{' '}
                                <span className="filename-truncado">{anexo.original_name || anexo.filename}</span>
                              </button>
                              {pdf.pendiente && anexo.id_usuario_subida === currentUser?.id_usuario && (
                                <button
                                  type="button"
                                  onClick={(e) => handleEliminarAnexo(pdf.id_eva, anexo.id_anexo, e)}
                                  className="btn-icon btn-icon-danger"
                                  title="Eliminar este anexo (subido por ti)"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted">Sin anexos</span>
                      )}
                    </td>
                    <td>
                      {pdf.pendiente ? (
                        <button
                          type="button"
                          onClick={() => abrirModalSubida(pdf)}
                          className="btn-icon btn-primary btn-sm"
                        >
                          <i className="fas fa-upload"></i> Subir Anexo
                        </button>
                      ) : (
                        <span className="text-muted" title="Esta ficha ya fue revisada, no se requieren más anexos">
                          <i className="fas fa-check"></i> Sin pendientes
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center">
                    No tienes fichas médicas disponibles todavía.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Subir Anexo */}
      <AnexoModal
        isOpen={modalSubirAbierto}
        onClose={() => setModalSubirAbierto(false)}
        idEva={evaluacionSeleccionada?.id_eva}
        pacienteNombre={paciente?.nombre}
        onSuccess={() => cargarDashboard()}
      />

      {/* Modal vista previa segura de anexo (Blob) */}
      {anexoVer && (
        <AnexoViewerModal
          idEva={anexoVer.idEva}
          anexo={anexoVer.anexo}
          onClose={handleCerrarModalAnexo}
          onDelete={
            anexoVer.pendiente && anexoVer.anexo.id_usuario_subida === currentUser?.id_usuario
              ? (e) => handleEliminarAnexo(anexoVer.idEva, anexoVer.anexo.id_anexo, e)
              : undefined
          }
        />
      )}
    </div>
  );
};

export default PacienteDashboard;
